import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  TimePicker,
  Button,
  DatePicker,
  Select,
  Radio,
  Checkbox,
  message,
} from "antd";
import TermSelect from "../TermSelect";
import "../../styles/components/facultyProfile/modal.scss";
import dayjs from "dayjs";

export default function FacultyProfileModal({
  open,
  mode,
  showDelete,
  title,
  config,
  initialEditValues,
  onCancel,
  onSubmit,
  onDelete,
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [courses, setCourses] = useState([]);

  const handleValuesChange = (_, allValues) => {
    if (!config?.calculate) return;
    const computed = config.calculate(allValues);
    form.setFieldsValue(computed);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      let formattedValues = { ...values };

      // Normalize yes/no values
      config?.fields?.forEach((field) => {
        if (field.type === "yes-no-radio") {
          if (formattedValues[field.name] === true) {
            formattedValues[field.name] = 1;
          } else if (formattedValues[field.name] === false) {
            formattedValues[field.name] = 0;
          }
        }
      });

      // Format date & time values
      config?.fields?.forEach((field) => {
        if (field.type === "date" && formattedValues[field.name]) {
          formattedValues[field.name] =
            formattedValues[field.name].format("YYYY-MM-DD");
        }

        if (field.type === "time" && formattedValues[field.name]) {
          formattedValues[field.name] =
            formattedValues[field.name].format("HH:mm:ss");
        }
      });

      // Clear hidden fields
      config?.fields?.forEach((field) => {
        if (field.showIf) {
          const dependentValue = values[field.showIf.field];
          const isVisible = dependentValue === field.showIf.value;

          if (!isVisible) {
            formattedValues[field.name] = null;
          }
        }
      });

      // Resolve the selected course's id so the backend can link this entry
      // to the course record (survives the course later being renamed).
      const courseDropdownField = config?.fields?.find(
        (field) => field.type === "course-dropdown",
      );
      if (courseDropdownField) {
        const selectedCourse = courses.find(
          (c) => c.course_number === formattedValues[courseDropdownField.name],
        );
        formattedValues.course_id = selectedCourse?.id ?? null;
      }

      setSubmitting(true);
      await onSubmit(formattedValues);
      message.success(
        mode === "edit" ? "Saved changes successfully." : "Added successfully.",
      );
    } catch (errorInfo) {
      message.error("Please fill out the fields correctly.");
      form.scrollToField(errorInfo.errorFields?.[0]?.name);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Are you sure you want to delete this item?",
      content: "This action cannot be undone.",
      okText: "Yes, delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setDeleting(true);
          await onDelete();
          message.success("Deleted successfully.");
        } catch (err) {
          message.error("Failed to delete item.");
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  useEffect(() => {
    if (open && mode === "edit" && initialEditValues) {
      const formattedValues = { ...initialEditValues };

      config?.fields?.forEach((field) => {
        if (field.type === "date" && formattedValues[field.name]) {
          formattedValues[field.name] = dayjs(formattedValues[field.name]);
        }

        if (field.type === "time" && formattedValues[field.name]) {
          formattedValues[field.name] = dayjs(
            `1970-01-01T${formattedValues[field.name]}`,
          );
        }

        if (field.type === "yes-no-radio") {
          if (formattedValues[field.name] === 1) {
            formattedValues[field.name] = true;
          } else if (formattedValues[field.name] === 0) {
            formattedValues[field.name] = false;
          }
        }
      });

      form.setFieldsValue(formattedValues); // Populate fields with existing values (if edit mode)

      const selectedCourse = courses.find(
        (c) => c.course_number === initialEditValues.course_number,
      );

      if (selectedCourse) {
        form.setFieldsValue({
          course_title: selectedCourse.course_title, // Automatically fill course title based on selected course number
        });
      }
    }
  }, [open, mode, initialEditValues, config, form, courses]);

  useEffect(() => {
    if (!open) return;
    if (courses.length > 0) return;

    const fetchCourses = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/course`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("api_token")}`,
          },
        });
        const data = await res.json();

        const sorted = data.sort((a, b) =>
          a.course_number.localeCompare(b.course_number),
        );

        setCourses(sorted);
      } catch (err) {
        console.error("Failed to fetch courses", err);
      }
    };

    fetchCourses();
  }, [open]);

  const renderFormItem = (field) => (
    <Form.Item
      key={field.name}
      name={field.name}
      label={field.type === "checkbox" ? null : field.label}
      preserve={true}
      valuePropName={field.type === "checkbox" ? "checked" : "value"}
      rules={[
        { required: field.required, message: "This field is required." },
        field.type === "integer" || field.type === "float"
          ? {
              validator: (_, value) => {
                if (value == null) return Promise.resolve();
                if (value < 0)
                  return Promise.reject("Value cannot be negative.");
                if (field.max != null && value > field.max)
                  return Promise.reject(`Value cannot exceed ${field.max}.`);
                return Promise.resolve();
              },
            }
          : {
              validator(_, value) {
                if (!value) return Promise.resolve();
                if (
                  value.toString().includes(";") ||
                  value.toString().includes("|")
                ) {
                  return Promise.reject(
                    new Error("Input cannot contain ';' or '|'"),
                  );
                }
                return Promise.resolve();
              },
            },
      ].filter(Boolean)}
    >
      {renderField(field)}
    </Form.Item>
  );

  const renderField = (field) => {
    switch (field.type) {
      case "integer":
        return <InputNumber style={{ width: "100%" }} precision={0} />;

      case "float":
        return <InputNumber style={{ width: "100%" }} />;

      case "time":
        return (
          <TimePicker
            style={{ width: "100%" }}
            use12Hours
            format="hh:mm A"
            showSecond={false}
          />
        );

      case "date":
        return <DatePicker style={{ width: "100%" }} />;

      case "term-select":
        return <TermSelect extended={field.extended} />;

      case "day-select":
        return (
          <Select
            showSearch
            style={{ width: "100%" }}
            options={[
              { label: "Monday", value: "Monday" },
              { label: "Tuesday", value: "Tuesday" },
              { label: "Wednesday", value: "Wednesday" },
              { label: "Thursday", value: "Thursday" },
              { label: "Friday", value: "Friday" },
              { label: "Saturday", value: "Saturday" },
              { label: "Sunday", value: "Sunday" },
            ]}
          />
        );

      case "yes-no-radio":
        return (
          <Radio.Group
            options={[
              { label: "Yes", value: true },
              { label: "No", value: false },
            ]}
          />
        );

      case "nomination-radio":
        return (
          <Radio.Group
            options={[
              { label: "Chair", value: "Chair" },
              { label: "Grant", value: "Grant" },
            ]}
          />
        );

      case "course-type-radio":
        return (
          <Radio.Group
            options={[
              { label: "Thesis", value: "Thesis" },
              { label: "Capstone", value: "Capstone" },
              { label: "Regular", value: "Regular" },
            ]}
          />
        );

      case "course-dropdown":
        return (
          <Select
            showSearch
            style={{ width: "100%" }}
            options={courses.map((c) => ({
              label: c.course_number,
              value: c.course_number,
            }))}
            filterOption={(input, option) =>
              option.label.toLowerCase().includes(input.toLowerCase())
            }
            onChange={(value) => {
              const selectedCourse = courses.find(
                (c) => c.course_number === value,
              );
              if (selectedCourse) {
                form.setFieldsValue({
                  course_title: selectedCourse.course_title,
                });
              }
            }}
          />
        );

      case "select":
        return (
          <Select
            showSearch={field.searchable}
            style={{ width: "100%" }}
            placeholder={`Select ${field.label}`}
            options={field.selectItems?.map((item) => ({
              label: item.label,
              value: item.name,
            }))}
            filterOption={
              field.searchable
                ? (input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                : undefined
            }
          />
        );

      case "checkbox":
        return <Checkbox>{field.label}</Checkbox>;

      case "read-only-text":
        return <Input disabled />;

      default:
        return <Input />;
    }
  };

  return (
    <Modal
      className="modal"
      open={open}
      onCancel={onCancel}
      destroyOnClose
      afterOpenChange={(open) => {
        if (!open) form.resetFields();
      }}
      footer={null}
      title={
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          {mode === "edit" ? "Edit Details" : "Add Item"}
          <span style={{ fontSize: "small", fontWeight: "normal" }}>
            {title}
          </span>
        </div>
      }
      styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
    >
      <Form
        form={form}
        layout="vertical"
        disabled={submitting}
        onValuesChange={handleValuesChange}
      >
        {config?.fields?.map((field) => {
          if (field.showIf) {
            return (
              <Form.Item
                shouldUpdate={(prev, curr) =>
                  prev[field.showIf.field] !== curr[field.showIf.field]
                }
                noStyle
                key={field.name}
              >
                {({ getFieldValue }) =>
                  getFieldValue(field.showIf.field) === field.showIf.value
                    ? renderFormItem(field)
                    : null
                }
              </Form.Item>
            );
          }

          return renderFormItem(field);
        })}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 20,
          }}
        >
          {mode === "edit" && showDelete && (
            <Button
              danger
              onClick={handleDelete}
              loading={deleting}
              disabled={deleting}
            >
              Delete item
            </Button>
          )}

          <Button
            type="primary"
            style={{ background: "#00573F" }}
            onClick={handleSubmit}
            loading={submitting}
            disabled={submitting}
          >
            {mode === "edit" ? "Save changes" : "Add"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
